import React, { useState, useEffect } from "react";
import {
  Fieldset,
  Legend,
  Field,
  Label,
  ErrorMessage,
} from "@/components/fieldset";
import { Input } from "@/components/input";
import { Select } from "@/components/select";
import { Button } from "@/components/button";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { useExamPaperList } from "@/apis/exam_papers/getExamPaperList";
import type { IExamPaper } from "@/apis/exam_papers/types";
import { Dialog } from "@headlessui/react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "@/components/table";
import { PaginationComponent } from "@/components/PaginationComponent";
import Popover from "@/components/Popover";
import ExamPaperSelectModal from "./ExamPaperSelectModal";
import ExamPaperItem from "./ExamPaperItem";

interface ExamSetFormProps {
  initialData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isLoading?: boolean;
  serverError?: string;
  isEditMode?: boolean;
}

const DEFAULT_ITEM = { exam_paper_id: "" };

const ExamSetForm: React.FC<ExamSetFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  serverError,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    exam_type: "SAT",
    items: [],
    ...initialData,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // 기존: 전체 examPapers 불러오기 및 filteredExamPapers 제거
  // 모달 상태 및 검색 관련 state 추가
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [modalPage, setModalPage] = useState(1);
  const [modalLimit] = useState(10); // 페이지당 10개
  const debounceTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Info popover state
  const [infoPopoverOpen, setInfoPopoverOpen] = useState(false);

  // 검색어 debounce 처리
  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
      setModalPage(1); // 검색어 바뀌면 1페이지로
    }, 300);
    // eslint-disable-next-line
  }, [search]);

  // 서버 페이징 기반 시험지 목록 패칭
  const { data: modalExamPapersData, isLoading: isModalExamPapersLoading } =
    useExamPaperList({
      page: modalPage,
      limit: modalLimit,
      title: debouncedSearch,
    });
  const modalExamPapers: IExamPaper[] = modalExamPapersData?.items || [];
  const modalTotalCount = modalExamPapersData?.totalCount || 0;
  const modalTotalPages = Math.ceil(modalTotalCount / modalLimit);

  // 이미 선택된 시험지는 모달에서 제외
  const selectedIds = formData.items.map((item: any) => item.exam_paper_id);
  const availableExamPapers = modalExamPapers.filter(
    (p) => !selectedIds.includes(p.id)
  );

  // Sync initialData
  useEffect(() => {
    if (!initialData || Object.keys(initialData).length === 0) return;
    const items =
      initialData.items && initialData.items.length > 0
        ? initialData.items.map((item: any) => ({
            exam_paper_id: item.exam_paper_id || "",
          }))
        : [];

    setFormData((prev: any) => ({
      ...prev,
      ...initialData,
      items: sortExamPapers(items),
    }));
    // eslint-disable-next-line
  }, [initialData, modalExamPapers]);

  // Field change handler
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Reset exam papers when exam type changes
    if (name === "exam_type" && value !== formData.exam_type) {
      setFormData((prev: any) => ({
        ...prev,
        [name]: value,
        items: [], // Reset exam papers list
      }));
    } else {
      setFormData((prev: any) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev: Record<string, string>) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const removeItem = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      items: prev.items.filter((_: any, i: number) => i !== index),
    }));
  };

  // Helper function to sort items: Reading and Writing first, then Math (SAT only)
  const sortExamPapers = (items: any[]) => {
    // Only apply sorting for SAT
    if (formData.exam_type !== "SAT") {
      return items; // No sorting for ACT/AP
    }

    const readingWritingItems = items.filter((item: any) => {
      const paperId = item.exam_paper_id;
      const paper = modalExamPapers.find((p) => p.id === paperId);
      return paper?.subject === "reading_writing";
    });

    const mathItems = items.filter((item: any) => {
      const paperId = item.exam_paper_id;
      const paper = modalExamPapers.find((p) => p.id === paperId);
      return paper?.subject === "math";
    });

    const otherItems = items.filter((item: any) => {
      const paperId = item.exam_paper_id;
      const paper = modalExamPapers.find((p) => p.id === paperId);
      return paper?.subject !== "reading_writing" && paper?.subject !== "math";
    });

    return [...readingWritingItems, ...mathItems, ...otherItems];
  };

  // Get existing paper types
  const getExistingPaperTypes = () => {
    return formData.items
      .map((item: any) => {
        const paper = modalExamPapers.find((p) => p.id === item.exam_paper_id);
        return paper?.subject;
      })
      .filter(Boolean);
  };

  // Check if we can add more papers
  const canAddMorePapers = () => {
    // Only apply restrictions for SAT
    if (formData.exam_type !== "SAT") {
      return true; // No restrictions for ACT/AP
    }

    const existingTypes = getExistingPaperTypes();
    const hasReadingWriting = existingTypes.includes("reading_writing");
    const hasMath = existingTypes.includes("math");
    return !hasReadingWriting || !hasMath;
  };

  // Validation & submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors: Record<string, string> = {};
    if (!formData.title) validationErrors.title = "Title is required";
    if (!formData.exam_type) validationErrors.exam_type = "Type is required";

    // Check for duplicate paper types (SAT only)
    if (formData.exam_type === "SAT") {
      const existingTypes = getExistingPaperTypes();
      const typeCount: Record<string, number> = {};
      existingTypes.forEach((type: string) => {
        typeCount[type] = (typeCount[type] || 0) + 1;
      });

      if (typeCount["reading_writing"] > 1) {
        validationErrors.exam_papers =
          "Only one Reading and Writing exam paper is allowed";
      }
      if (typeCount["math"] > 1) {
        validationErrors.exam_papers = "Only one Math exam paper is allowed";
      }
    }

    formData.items.forEach((item: any, idx: number) => {
      if (!item.exam_paper_id)
        validationErrors[`exam_paper_id_${idx}`] = "Exam Paper is required";
    });
    if (formData.items.length === 0) {
      validationErrors.exam_papers = "At least one exam paper is required";
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    // Assign order based on array index
    const itemsWithOrder = formData.items.map((item: any, idx: number) =>
      item && typeof item === "object"
        ? { ...item, order: idx + 1 }
        : { order: idx + 1 }
    );
    onSubmit({ ...(formData || {}), items: itemsWithOrder });
  };

  // Helper: get exam paper info by id
  const getExamPaperInfo = (id: string) =>
    modalExamPapers.find((p) => p.id === id);

  // 시험지 선택 핸들러
  const handleAddExamPaper = (paper: IExamPaper) => {
    // Only check for duplicate types in SAT
    if (formData.exam_type === "SAT") {
      const existingTypes = getExistingPaperTypes();
      if (existingTypes.includes(paper.subject)) {
        return; // Don't add if type already exists for SAT
      }
    }

    const newItems = [...formData.items, { exam_paper_id: paper.id }];
    const sortedItems = sortExamPapers(newItems);

    setFormData((prev: any) => ({
      ...prev,
      items: sortedItems,
    }));
    setIsModalOpen(false);
    setSearch("");
    setModalPage(1);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8" autoComplete="off">
      {serverError && (
        <div className="p-4 bg-red-50 text-red-600 rounded-md">
          {serverError}
        </div>
      )}
      {errors.exam_papers && (
        <div className="p-4 bg-red-50 text-red-600 rounded-md">
          {errors.exam_papers}
        </div>
      )}
      {/* Basic Information */}
      <Fieldset>
        <Legend>Basic Information</Legend>
        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Field>
            <Label htmlFor="title" className="mb-1">
              Title*
            </Label>
            <Input
              id="title"
              name="title"
              placeholder="Enter exam set title"
              value={formData.title}
              onChange={handleChange}
              aria-invalid={!!errors.title}
              required
            />
            {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
          </Field>
          <Field>
            <Label htmlFor="exam_type" className="mb-1">
              Exam Type*
            </Label>
            <Select
              id="exam_type"
              name="exam_type"
              value={formData.exam_type}
              onChange={handleChange}
              required
            >
              <option value="SAT">SAT</option>
              <option value="ACT">ACT</option>
              <option value="AP">AP</option>
            </Select>
            {errors.exam_type && (
              <ErrorMessage>{errors.exam_type}</ErrorMessage>
            )}
          </Field>
        </div>
      </Fieldset>
      {/* Included Exam Papers */}
      {!isEditMode && (
        <Fieldset>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Legend>Included Exam Papers</Legend>
              <Popover
                open={infoPopoverOpen}
                onOpenChange={setInfoPopoverOpen}
                placement="bottom-start"
                title="Exam Paper Rules"
                className="font-sans"
                trigger={
                  <button
                    type="button"
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                    aria-label="Show exam paper rules"
                  >
                    <InformationCircleIcon className="h-5 w-5" />
                  </button>
                }
              >
                <div className="space-y-3 text-sm max-w-md">
                  {formData.exam_type === "SAT" ? (
                    <>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-gray-900">
                          Adding Exam Papers (SAT)
                        </h4>
                        <ul className="space-y-1 text-gray-700">
                          <li>
                            • Maximum of <strong>1 Reading and Writing</strong>{" "}
                            exam paper
                          </li>
                          <li>
                            • Maximum of <strong>1 Math</strong> exam paper
                          </li>
                          <li>• At least one exam paper is required</li>
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-gray-900">
                          Automatic Ordering
                        </h4>
                        <ul className="space-y-1 text-gray-700">
                          <li>
                            • <strong>Reading and Writing</strong> will always
                            appear first
                          </li>
                          <li>
                            • <strong>Math</strong> will always appear second
                          </li>
                          <li>• Order cannot be manually changed</li>
                        </ul>
                      </div>
                      <div className="pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500">
                          This ensures consistent exam structure and proper
                          sequencing.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900">
                        Adding Exam Papers ({formData.exam_type})
                      </h4>
                      <ul className="space-y-1 text-gray-700">
                        <li>• No restrictions on paper types</li>
                        <li>• Multiple papers of the same type are allowed</li>
                        <li>• Papers will be added in selection order</li>
                        <li>• At least one exam paper is required</li>
                      </ul>
                    </div>
                  )}
                </div>
              </Popover>
            </div>
            <Button
              type="button"
              onClick={() => setIsModalOpen(true)}
              disabled={!canAddMorePapers()}
            >
              Add Exam Paper
            </Button>
          </div>
          <div className="mt-4">
            {formData.items.length === 0 ? (
              <div className="text-center text-zinc-400 py-8 border border-dashed border-gray-200 rounded-md bg-gray-50">
                No exam papers added yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="min-w-full">
                  <TableHead>
                    <TableRow>
                      <TableHeader>Title</TableHeader>
                      <TableHeader>Subject</TableHeader>
                      <TableHeader>Section</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.items.map((item: any, index: number) => (
                      <ExamPaperItem
                        key={index}
                        index={index}
                        examPaperId={item.exam_paper_id}
                        onRemove={() => removeItem(index)}
                        isLastItem={formData.items.length === 1}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          {/* Add Exam Paper Modal */}
          <ExamPaperSelectModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSelect={handleAddExamPaper}
            excludeIds={formData.items.map((item: any) => item.exam_paper_id)}
            existingTypes={getExistingPaperTypes()}
            examType={formData.exam_type}
          />
        </Fieldset>
      )}
      {/* Form Actions */}
      <div className="flex justify-end space-x-4 pt-4">
        <Button type="button" onClick={onCancel} disabled={isLoading} plain>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {isLoading
            ? "Saving..."
            : isEditMode
            ? "Save Changes"
            : "Create Exam Set"}
        </Button>
      </div>
    </form>
  );
};

export default ExamSetForm;
